import React from 'react';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import { cn } from '@core/utils/cn';
import { Spinner } from '../../../../components/spinner';
import { ActivityDto } from '../../../../core/api/FitnessActivityClient';
import { SelectedWorkout } from '../types';

interface WorkoutPickerProps {
    workouts: ActivityDto[];
    isLoading: boolean;
    error: string | null;
    hasMore: boolean;
    onLoadMore: () => void;
    selected: SelectedWorkout[];
    maxSelected: number;
    onToggle: (workout: ActivityDto) => void;
}

const localDate = (iso: string): string => iso.slice(0, 10);
const localTime = (iso: string): string => iso.slice(11, 16);

const formatRange = (from: string, to: string): string => {
    const fromDate = localDate(from);
    const fromTime = localTime(from);
    const toDate = localDate(to);
    const toTime = localTime(to);
    if (fromDate === toDate) {
        return `${fromDate} ${fromTime} - ${toTime}`;
    }
    return `${fromDate} ${fromTime} - ${toDate} ${toTime}`;
};

export function WorkoutPicker({
    workouts,
    isLoading,
    error,
    hasMore,
    onLoadMore,
    selected,
    maxSelected,
    onToggle
}: WorkoutPickerProps) {
    const selectedIds = new Set(selected.map((s) => s.id));
    const atLimit = selected.length >= maxSelected;

    return (
        <div>
            <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Select Workouts ({selected.length}/{maxSelected})
                </span>
                {atLimit && (
                    <span className="text-xs text-warning">
                        Max {maxSelected} tracks — uncheck to add more
                    </span>
                )}
            </div>

            {error && (
                <Alert variant="destructive" className="mb-2">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <div className="max-h-[280px] overflow-y-auto rounded-lg border">
                {workouts.length === 0 && !isLoading && (
                    <div className="p-4 text-xs text-muted-foreground">
                        No workouts for this user.
                    </div>
                )}
                {workouts.map((w) => {
                    const isChecked = selectedIds.has(w.id);
                    const disabled = !isChecked && atLimit;
                    return (
                        <div
                            key={w.id}
                            onClick={() => {
                                if (disabled) return;
                                onToggle(w);
                            }}
                            className={cn(
                                'flex items-center gap-2 border-b border-border/50 px-2 py-1 font-mono text-xs',
                                disabled
                                    ? 'cursor-not-allowed opacity-40'
                                    : 'cursor-pointer hover:bg-primary/5'
                            )}
                        >
                            <Checkbox
                                checked={isChecked}
                                disabled={disabled}
                                onClick={(e) => e.stopPropagation()}
                                onCheckedChange={() => onToggle(w)}
                            />
                            <span className="min-w-[200px] text-foreground">
                                {formatRange(
                                    w.dateTimeRange.from,
                                    w.dateTimeRange.to
                                )}
                            </span>
                            <span className="min-w-[110px] text-muted-foreground">
                                {w.workoutTypeV2 ?? '—'}
                            </span>
                            <span
                                className={cn(
                                    'flex-1',
                                    w.rejectionReason
                                        ? 'text-red-400'
                                        : 'text-muted-foreground'
                                )}
                            >
                                {w.rejectionReason ?? ''}
                            </span>
                            <span className="min-w-[70px] text-right text-muted-foreground/70">
                                #{w.id}
                            </span>
                            {w.rejected && !w.rejectionReason && (
                                <Badge
                                    variant="outline"
                                    className="h-[18px] border-destructive text-[10px] text-destructive"
                                >
                                    rejected
                                </Badge>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="mt-2 flex items-center justify-center gap-2">
                {isLoading && <Spinner size="sm" />}
                {!isLoading && hasMore && (
                    <Button size="sm" variant="outline" onClick={onLoadMore}>
                        Load more
                    </Button>
                )}
                {!hasMore && workouts.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                        All {workouts.length} workouts loaded
                    </span>
                )}
            </div>
        </div>
    );
}
